import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { ChatConversationDto, ChatLastMessage, ChatMessageDto, ChatMessagePage, ChatUnreadSummary } from '@nexops/shared';
import { isMuted } from '../lib/chat-utils';
import { chatKeys } from './chat.api';

export type MessagesData = InfiniteData<ChatMessagePage, string | undefined>;

const LIST_KEYS = [chatKeys.conversations(false), chatKeys.conversations(true)] as const;

/** Przypięte najpierw, potem najnowsza aktywność. */
export function sortConversations(a: ChatConversationDto, b: ChatConversationDto): number {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
  return Date.parse(b.lastMessageAt) - Date.parse(a.lastMessageAt);
}

export function findConversation(qc: QueryClient, id: string): ChatConversationDto | undefined {
  const detail = qc.getQueryData<ChatConversationDto>(chatKeys.conversation(id));
  if (detail) return detail;
  for (const key of LIST_KEYS) {
    const hit = qc.getQueryData<ChatConversationDto[]>(key)?.find((c) => c.id === id);
    if (hit) return hit;
  }
  return undefined;
}

/** Wstawia / podmienia rozmowę we wszystkich listach i w cache szczegółów (archiwalne trafiają na listę archiwum). */
export function upsertConversation(qc: QueryClient, conv: ChatConversationDto): void {
  qc.setQueryData(chatKeys.conversation(conv.id), conv);
  for (const archived of [false, true]) {
    qc.setQueryData<ChatConversationDto[]>(chatKeys.conversations(archived), (old) => {
      if (!old) return old;
      const rest = old.filter((c) => c.id !== conv.id);
      return conv.archived === archived ? [...rest, conv].sort(sortConversations) : rest;
    });
  }
}

export function patchConversation(qc: QueryClient, id: string, fn: (c: ChatConversationDto) => ChatConversationDto): void {
  const detail = qc.getQueryData<ChatConversationDto>(chatKeys.conversation(id));
  if (detail) qc.setQueryData(chatKeys.conversation(id), fn(detail));
  for (const key of LIST_KEYS) {
    qc.setQueryData<ChatConversationDto[]>(key, (old) =>
      old?.some((c) => c.id === id) ? old.map((c) => (c.id === id ? fn(c) : c)).sort(sortConversations) : old,
    );
  }
}

export function removeConversation(qc: QueryClient, id: string): void {
  for (const key of LIST_KEYS) {
    qc.setQueryData<ChatConversationDto[]>(key, (old) => old?.filter((c) => c.id !== id));
  }
  qc.removeQueries({ queryKey: chatKeys.conversation(id) });
  qc.removeQueries({ queryKey: chatKeys.messages(id) });
}

/** Dodaje wiadomość do cache wątku (jeśli wczytany) albo podmienia istniejącą o tym samym id. */
export function upsertMessage(qc: QueryClient, conversationId: string, message: ChatMessageDto): void {
  qc.setQueryData<MessagesData>(chatKeys.messages(conversationId), (old) => {
    if (!old || old.pages.length === 0) return old;
    const exists = old.pages.some((p) => p.items.some((m) => m.id === message.id));
    if (exists) {
      return { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map((m) => (m.id === message.id ? message : m)) })) };
    }
    const [newest, ...older] = old.pages;
    return { ...old, pages: [{ ...newest!, items: [...newest!.items, message] }, ...older] };
  });
}

/** Miękkie usunięcie wiadomości w cache (zdarzenie `message.deleted` niesie tylko id). */
export function markMessageDeleted(qc: QueryClient, conversationId: string, messageId: string): void {
  qc.setQueryData<MessagesData>(chatKeys.messages(conversationId), (old) =>
    old
      ? {
          ...old,
          pages: old.pages.map((p) => ({
            ...p,
            items: p.items.map((m) =>
              m.id === messageId ? { ...m, body: '', attachments: [], reactions: [], deletedAt: m.deletedAt ?? new Date().toISOString() } : m,
            ),
          })),
        }
      : old,
  );
}

export function setUnread(qc: QueryClient, fn: (s: ChatUnreadSummary) => ChatUnreadSummary): void {
  qc.setQueryData<ChatUnreadSummary>(chatKeys.unread, (old) => (old ? fn(old) : old));
}

function lastMessageOf(message: ChatMessageDto): ChatLastMessage {
  return {
    id: message.id,
    senderId: message.sender.id,
    senderName: `${message.sender.firstName} ${message.sender.lastName}`,
    snippet: message.body.slice(0, 120),
    kind: message.kind,
    createdAt: message.createdAt,
  };
}

/**
 * Wspólna obsługa nowej wiadomości (zdarzenie ze strumienia albo odpowiedź na własny POST):
 * wątek, podgląd na liście, licznik nieprzeczytanych i podsumowanie. Zwraca `false`, gdy rozmowy nie ma w cache.
 */
export function applyIncomingMessage(qc: QueryClient, message: ChatMessageDto, meId: string): boolean {
  upsertMessage(qc, message.conversationId, message);
  const conv = findConversation(qc, message.conversationId);
  if (!conv) return false;
  if (conv.lastMessage?.id === message.id) return true; // już policzona (np. odpowiedź POST + zdarzenie)

  const newlyUnread = message.sender.id !== meId && message.kind !== 'SYSTEM';
  patchConversation(qc, conv.id, (c) => ({
    ...c,
    lastMessage: lastMessageOf(message),
    lastMessageAt: message.createdAt,
    unreadCount: newlyUnread ? c.unreadCount + 1 : c.unreadCount,
  }));
  if (newlyUnread && !isMuted(conv) && !conv.archived) {
    setUnread(qc, (s) => ({ total: s.total + 1, conversations: s.conversations + (conv.unreadCount === 0 ? 1 : 0) }));
  }
  return true;
}

/** Edycja / usunięcie: odświeża podgląd na liście, gdy dotyczy ostatniej wiadomości. */
export function applyMessageUpdate(qc: QueryClient, message: ChatMessageDto): void {
  upsertMessage(qc, message.conversationId, message);
  const conv = findConversation(qc, message.conversationId);
  if (conv?.lastMessage?.id === message.id) {
    patchConversation(qc, conv.id, (c) => ({ ...c, lastMessage: lastMessageOf(message) }));
  }
}

/** Zerowanie licznika rozmowy (po POST /read lub zdarzeniu `read` z innej karty). */
export function clearConversationUnread(qc: QueryClient, id: string): void {
  const conv = findConversation(qc, id);
  if (!conv || conv.unreadCount === 0) return;
  patchConversation(qc, id, (c) => ({ ...c, unreadCount: 0 }));
  if (!isMuted(conv) && !conv.archived) {
    setUnread(qc, (s) => ({ total: Math.max(0, s.total - conv.unreadCount), conversations: Math.max(0, s.conversations - 1) }));
  }
}
